import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import SectionIconTile from "../../../Components/SectionIconTile";
import { VehicleIcon } from "../../../Components/Icons/SectionIcons";
import { formatDateTime } from "../../../Helper/DatePicker/DateFormatters";
import { getLocalDateFromUTC } from "../../../Helper/DatePicker/DateConverter";

const METRICS = [
    { key: "odometerKm", label: "Odometer", unit: "km" },
    { key: "soc", label: "SOC", unit: "%" },
    { key: "maxCellTemp", label: "Max cell temp", unit: "°C" },
    { key: "minCellTemp", label: "Min cell temp", unit: "°C" },
    { key: "maxCellVoltage", label: "Max cell voltage", unit: "V" },
    { key: "minCellVoltage", label: "Min cell voltage", unit: "V" },
    { key: "ambientTemp", label: "Ambient temp", unit: "°C" },
    { key: "voltageDiff", label: "Voltage diff", unit: "V" },
    { key: "motorTemp", label: "Motor Temp", unit: "°C" },
    { key: "sohBms", label: "BMS SOH", unit: "%" },
];

const withUnit = (val, unit) => {
    if (val === null || val === undefined || val === "") return null;
    const sep = unit === "%" || unit === "°C" ? "" : " ";
    return `${val}${sep}${unit}`;
};

const DeltaChip = ({ delta, unit }) => {
    if (!delta) return null;
    const { direction, value } = delta;
    // noData -> empty placeholder pill (matches the Figma "Ambient temp" card).
    let label = " ";
    if (direction === "up") label = `↑ ${withUnit(value, unit)}`;
    else if (direction === "down") label = `↓ ${withUnit(value, unit)}`;
    else if (direction === "none") label = "No change";

    return (
        <Box
            sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#475467",
                backgroundColor: "#F6F7F9",
                border: "1px solid #E4E7EC",
                borderRadius: "6px",
                padding: "2px 10px",
                minWidth: "36px",
                textAlign: "center",
                whiteSpace: "nowrap",
            }}
        >
            {label}
        </Box>
    );
};

// Metric card (Figma): grey header strip (label + delta pill) over a white body
// with the Non-operational / Current rows.
const MetricTile = ({ metric, current, heldUp, delta }) => {
    const heldUpDisplay = withUnit(heldUp?.[metric.key], metric.unit);
    const currentDisplay = withUnit(current?.[metric.key], metric.unit);

    return (
        <Box
            sx={{
                border: "1px solid #EAECF0",
                borderRadius: "10px",
                overflow: "hidden",
                backgroundColor: "#fff",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    backgroundColor: "#F6F7F9",
                    padding: "10px 14px",
                    borderBottom: "1px solid #EAECF0",
                }}
            >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A" }}>
                    {metric.label}
                </Typography>
                <DeltaChip delta={delta} unit={metric.unit} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #F2F4F7" }}>
                <Typography sx={{ fontSize: "12px", color: "#8D8D8D" }}>Issue reported</Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: heldUpDisplay ? "#1A1A1A" : "#B0B0B0" }}>
                    {heldUpDisplay || "no data"}
                </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", padding: "10px 14px" }}>
                <Typography sx={{ fontSize: "12px", color: "#8D8D8D" }}>Latest</Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: currentDisplay ? "#1A1A1A" : "#B0B0B0" }}>
                    {currentDisplay || "no data"}
                </Typography>
            </Box>
        </Box>
    );
};

// Two-point Bus Information tiles (held up vs current) from metrics-preview.
export default function BusInformationSection({
    preview = null,
    loading = false,
    busHeldUpAt = null,
}) {
    return (
        <Box
            sx={{
                border: "1px solid #EAEAEA",
                borderRadius: "12px",
                padding: "24px 28px",
                backgroundColor: "#fff",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "18px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <SectionIconTile icon={<VehicleIcon />} />
                    <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1A1A1A" }}>
                        Vehicle Information
                    </Typography>
                </Box>
                {(preview || busHeldUpAt) && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            border: "1px solid #EAECF0",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            backgroundColor: "#F6F7F9",
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontSize: "10px", color: "#8D8D8D", letterSpacing: "0.4px" }}>
                                ISSUE REPORTED DATE
                            </Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A" }}>
                                {/* busHeldUpAt is the user-local picker value; preview.heldUpAt
                                    is backend UTC and needs converting to the user's zone. */}
                                {busHeldUpAt
                                    ? formatDateTime(busHeldUpAt, "DD MMM YYYY, hh:mm A")
                                    : preview?.heldUpAt
                                    ? getLocalDateFromUTC(preview.heldUpAt, "DD MMM YYYY, hh:mm A")
                                    : "—"}
                            </Typography>
                        </Box>
                        <ArrowRightAltIcon sx={{ color: "#8D8D8D" }} />
                        <Box>
                            <Typography sx={{ fontSize: "10px", color: "#8D8D8D", letterSpacing: "0.4px" }}>
                                CURRENT DATE
                            </Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A" }}>
                                {/* capturedAt is backend UTC -> convert to the user's zone. */}
                                {preview?.capturedAt
                                    ? getLocalDateFromUTC(preview.capturedAt, "DD MMM YYYY, hh:mm A")
                                    : "—"}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {!busHeldUpAt ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>
                        Select the "Issue reported date time" in Timeline to load vehicle information.
                    </Typography>
                </Box>
            ) : loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CartLoader />
                </Box>
            ) : !preview ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>
                        Unable to load bus information for the selected time.
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr 1fr",
                            sm: "repeat(3, 1fr)",
                            md: "repeat(5, 1fr)",
                        },
                        gap: "12px",
                    }}
                >
                    {METRICS.map((metric) => (
                        <MetricTile
                            key={metric.key}
                            metric={metric}
                            current={preview.current}
                            heldUp={preview.heldUp}
                            delta={preview.delta?.[metric.key]}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}
